// Import CSS
import './AssessmentFormOrderGroup.scss'

// React and Third Party Libraries
import { useContext, useEffect, useState } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import cn from 'classnames'
import AssessmentFormContext from 'components/common/contexts/AssessmentFormContext'

/**
 * Assessment Form Screen Check Group component: used to render an group of checkboxes or radio buttons
 * @param {Object} initProps - initial props
 * @param {Object} initProps.questionData - question data to be used to build the questions. Must follow the assessment schema
 * @param {Array} initProps.questionData.options - options for the question
 * @param {String} initProps.questionData.options[].key - key value for the option
 * @param {String} initProps.questionData.options[].label - label for the option
 * @param {String} initProps.questionData.type - type for the option (radio or checkbox)
 * @param {String} initProps.questionData.label - label for the question
 * @param {Number} initProps.questionIndex - index of the question
 * @param {String} initProps.value - value of the input
 * @param {Array} initProps.values - values of the input (for multiple choice)
 * @param {Function} initProps.handleInputChange - function to handle input change
 * @param {String} initProps.handleInputChange.value - value of the input
 * @param {Array} initProps.handleInputChange.values - values of the input (for multiple choice)
 * @param {String} initProps.handleInputChange.name - name of the input
 * @returns {JSX.Element} - Assessment Form Screen Field Group component
 */
export default function AssessmentFormOrderGroup({
  questionData,
  questionIndex,
  handleInputChange = () => ({ value: '', values: [], name: '' }),
  isVisible = true
}) {
  const { answers, formData } = useContext(AssessmentFormContext)

  // State
  const [orderOptions, setOrderOptions] = useState([])

  // Get the question name, type, and options
  const questionName = questionData.name || ''
  const questionType = questionData.type || ''
  const questionTypeGroup = questionType.split('-')[0]

  /**
   * Helper function to reorder the list
   * @param {Array} list The list to reorder
   * @param {Number} startIndex The index to start from
   * @param {Number} endIndex The index to end at
   * @returns
   */
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  /**
   * Handler for drag and drop
   * @param {Object} result The result of the drag and drop
   */
  const onDragEnd = result => {
    // // dropped outside the list
    if (!result.destination) {
      return
    }

    const items = reorder(
      orderOptions,
      result.source.index,
      result.destination.index
    )

    setOrderOptions(items)
    handleInputChange({
      values: items.map(item => item.key),
      name: questionName
    })
  }

  // If the questionData has options and the options has a `depends-on` property, use the value of that property to
  // Get the options for the current question from the answers that match the `depends-on` property value
  useEffect(() => {
    // If the field is not visible, we don't have answers, or it's empty return
    if (!isVisible || !answers || !Object.keys(answers).length) return

    // If the questionData has options and the options has a `depends-on` property, use the value of that property to
    // Get the options for the current question from the answers that match the `depends-on` property value
    if (questionData.options && questionData.options['depends-on']) {
      const dependsOn = questionData.options['depends-on']
      const dependsOnAnswer = answers[dependsOn]

      // If we don't have a depends on answer, return
      if (!dependsOnAnswer) return

      // Get the order options by getting the screen a field group with the same name as the depends on answer
      const fieldGroup = formData.screens
        .map(screen => screen['field-groups'])
        .flat()
        .filter(fieldGroup => fieldGroup.name === dependsOn)

      // Then get the options from that field group that match the depends on answer
      const fieldOptions = fieldGroup
        .map(fieldGroup => fieldGroup.options)
        .flat()
        .filter(option => dependsOnAnswer.includes(option.key))

      // If the number of options is different, update the state
      if (fieldOptions.length !== orderOptions.length) {
        setOrderOptions(fieldOptions)
      }
    }
  }, [isVisible, answers, questionData, questionName, formData, orderOptions])

  // If we don't have screen data, return null
  if (!questionData || !isVisible) return null

  return (
    <div
      className={cn('assessment-form-order-group', questionTypeGroup)}
      key={questionIndex}
    >
      {questionData.label && (
        <p className='assessment-form-check-group-label muted'>
          {questionData.label}
        </p>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='droppable'>
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {orderOptions.map((item, index) => (
                <Draggable key={item.key} draggableId={item.key} index={index}>
                  {(provided, snapshot) => (
                    <div
                      className={cn('assessment-form-order-option', {
                        dragging: snapshot.isDragging
                      })}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <span className='assessment-form-order-option-counter'>
                        {index + 1}
                      </span>
                      {item.label}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        {/* <Droppable droppableId='droppable'>
          {orderOptions.map((option, optionIndex) => (
            <Draggable key={optionIndex}>
              <div className='assessment-form-order-option'>{option.label}</div>
            </Draggable>
            // <Form.Check
            //   className={cn('assessment-form-question-option', {
            //     [questionTypeGroup]: true
            //   })}
            //   label={option.label}
            //   key={optionIndex}
            //   id={`${questionName}-${optionIndex}`}
            //   name={questionName}
            //   value={option.key}
            //   onClick={onClick}
            //   type={questionTypeGroup}
            //   defaultChecked={
            //     questionTypeGroup === 'checkbox'
            //       ? questionValues.includes(option.key)
            //       : questionValue === option.key
            //   }
            // />
          ))}
        </Droppable> */}
      </DragDropContext>
    </div>
  )
}
